import { TYPES } from '../render/constants';
import { applyGridSnapping } from './grid';
import { sortByRenderIndex as _sortByRenderIndex } from './interaction/node';

// Basic alignment/snap system
const DEBUG_ALIGN = false;
function getWorldSnapThreshold(stageContext) {
  try {
    const scale = stageContext?.providers?.scale?.scale || 1;
    const canvas = stageContext?.context?.canvas;
    const minScreenDim = Math.max(1, Math.min(canvas?.width || 0, canvas?.height || 0));
    const px = Math.max(6, Math.min(20, Math.round(minScreenDim * 0.008)));
    return px / scale;
  } catch {
    return 8;
  }
}

export function hasFrameAncestor(node, stageContext) {
  if (!node) return false;
  const ancestors = stageContext.api.getAncenstors(node.id) || [];
  return ancestors.some((a) => a.type === TYPES.frame);
}

export function getFrameAncestor(node, stageContext) {
  if (!node) return null;
  const ancestors = stageContext.api.getAncenstors(node.id) || [];
  return ancestors.find((a) => a.type === TYPES.frame) || null;
}

function getCandidateFrames(stageContext, movingNodes) {
  const frames = new Set();
  (movingNodes || []).forEach((m) => {
    const n = m.node || m;
    const f = getFrameAncestor(n, stageContext);
    if (f) frames.add(f);
  });
  return Array.from(frames);
}

function buildCandidateRects(stageContext, movingNodes) {
  const movingIdSet = new Set((movingNodes || []).map((m) => (m.node ? m.node.id : m.id)));
  const frames = getCandidateFrames(stageContext, movingNodes);
  const frameIdSet = new Set(frames.map((f) => f.id));

  // limit candidates to nodes within the same frame(s). If no frame, use all visible nodes
  const allNodes = stageContext.api.getNodes() || [];
  const candidates = [];
  for (const node of allNodes) {
    if (node.hidden === true || node.visible === false) continue;
    if (movingIdSet.has(node.id)) continue;
    // Exclude descendants of moving nodes to avoid self-snap
    const ancestors = stageContext.api.getAncenstors(node.id) || [];
    if (ancestors.some((a) => movingIdSet.has(a.id))) continue;
    if (frameIdSet.size) {
      const f = ancestors.find((a) => a.type === TYPES.frame);
      if (!f || !frameIdSet.has(f.id)) continue;
    }
    candidates.push({ node, rect: stageContext.api.getNodeRegion(node).rect });
  }

  // Include frame borders themselves as snap targets
  for (const f of frames) {
    candidates.push({ node: f, rect: stageContext.api.getNodeRegion(f).rect, isFrame: true });
  }
  return candidates;
}

function computeSnapDeltaForAxis(currentValueTriplet, candidateValues, threshold, guideMap = null) {
  // currentValueTriplet: { start, center, end }
  // candidateValues: array of numbers
  let bestDelta = 0;
  let bestDist = Infinity;
  let bestType = null; // 'start' | 'center' | 'end'
  let bestTarget = null;
  let bestRefs = null;
  const keyOf = (v) => `${Math.round(v * 10000) / 10000}`;
  for (const v of candidateValues) {
    const ds = v - currentValueTriplet.start;
    const dc = v - currentValueTriplet.center;
    const de = v - currentValueTriplet.end;
    const cand = [
      { t: 'start', d: ds, a: Math.abs(ds) },
      { t: 'center', d: dc, a: Math.abs(dc) },
      { t: 'end', d: de, a: Math.abs(de) },
    ];
    for (const c of cand) {
      if (c.a < bestDist && c.a <= threshold) {
        bestDist = c.a;
        bestDelta = c.d;
        bestType = c.t;
        bestTarget = v;
        bestRefs = guideMap ? guideMap.get(keyOf(v)) || null : null;
      }
    }
  }
  return {
    delta: bestDelta,
    matched: Number.isFinite(bestDist),
    type: bestType,
    target: bestTarget,
    refs: bestRefs,
  };
}

function collectCandidateGuideValues(candidates) {
  const vx = []; // vertical x positions
  const vy = []; // horizontal y positions
  const vxMap = new Map(); // x value -> [{ rect, role }]
  const vyMap = new Map(); // y value -> [{ rect, role }]
  const keyOf = (v) => `${Math.round(v * 10000) / 10000}`;
  const add = (map, key, rect, role) => {
    if (!map.has(key)) map.set(key, []);
    map.get(key).push({ rect, role });
  };
  for (const c of candidates) {
    const r = c.rect;
    const x0 = r.x,
      x1 = r.x + r.w,
      xc = r.x + r.w / 2;
    const y0 = r.y,
      y1 = r.y + r.h,
      yc = r.y + r.h / 2;
    vx.push(x0, x1, xc);
    vy.push(y0, y1, yc);
    add(vxMap, keyOf(x0), r, 'start');
    add(vxMap, keyOf(x1), r, 'end');
    add(vxMap, keyOf(xc), r, 'center');
    add(vyMap, keyOf(y0), r, 'start');
    add(vyMap, keyOf(y1), r, 'end');
    add(vyMap, keyOf(yc), r, 'center');

    // Text-specific guides: baseline and cap-height approximations for better text alignment
    try {
      const n = c.node;
      if (n && n.type === TYPES.text) {
        const fontSize = Number(n.fontSize) || 16;
        const ascent = Math.max(0, Math.min(r.h, fontSize * 0.8));
        const cap = Math.max(0, Math.min(r.h, fontSize * 0.7));
        const baselineY = r.y + ascent;
        const capY = r.y + Math.max(0, cap * 0.95);
        vy.push(baselineY, capY);
        add(vyMap, keyOf(baselineY), r, 'text-baseline');
        add(vyMap, keyOf(capY), r, 'text-cap');
      }
    } catch {}
  }
  return { vx, vy, vxMap, vyMap };
}

// Build spacing (gap) guide values around candidate edges using common spacing steps
function collectGapGuideValues(candidates) {
  const gapSteps = [4, 6, 8, 12, 16, 20, 24, 32, 40, 48];
  const gx = []; // positions for left/right edges to land on to realize target gaps
  const gy = []; // positions for top/bottom edges to land on to realize target gaps
  const gxMap = new Map();
  const gyMap = new Map();
  const keyOf = (v) => `${Math.round(v * 10000) / 10000}`;
  const add = (map, key, rect, role, gap) => {
    if (!map.has(key)) map.set(key, []);
    map.get(key).push({ rect, role, gap });
  };
  for (const c of candidates) {
    const r = c.rect;
    for (const g of gapSteps) {
      // Horizontal: snap left to (right + g), or right to (left - g)
      gx.push(r.x + r.w + g);
      add(gxMap, keyOf(r.x + r.w + g), r, 'gap-right', g);
      gx.push(r.x - g);
      add(gxMap, keyOf(r.x - g), r, 'gap-left', g);
      // Vertical: snap top to (bottom + g), or bottom to (top - g)
      gy.push(r.y + r.h + g);
      add(gyMap, keyOf(r.y + r.h + g), r, 'gap-bottom', g);
      gy.push(r.y - g);
      add(gyMap, keyOf(r.y - g), r, 'gap-top', g);
    }
  }
  return { gx, gy, gxMap, gyMap };
}

function buildGuideLines(newBox, snapX, snapY) {
  const lines = [];
  if (snapX && snapX.matched && typeof snapX.target === 'number') {
    // vertical line at target x, spanning overlap with moving box for clarity
    lines.push({
      type: 'vertical',
      left: snapX.target,
      top: newBox.top,
      size: newBox.height,
      confidence: snapX.type === 'center' ? 0.95 : 0.9,
      isCenter: snapX.type === 'center',
      source: 'obj',
    });
  }
  if (snapY && snapY.matched && typeof snapY.target === 'number') {
    lines.push({
      type: 'horizontal',
      left: newBox.left,
      top: snapY.target,
      size: newBox.width,
      confidence: snapY.type === 'center' ? 0.95 : 0.9,
      isCenter: snapY.type === 'center',
      source: 'obj',
    });
  }
  return lines;
}

function computeMoveSnap(stageContext, movingBox, movingNodes, deltaX, deltaY, cache) {
  const threshold = getWorldSnapThreshold(stageContext);
  const candidates = cache?.candidates || buildCandidateRects(stageContext, movingNodes);
  const guides =
    cache?.guides ||
    (() => {
      const g = collectCandidateGuideValues(candidates);
      const gaps = collectGapGuideValues(candidates);
      return { ...g, ...gaps };
    })();

  // IMPORTANT: box passed in update() is already at absolute (moved) position.
  // Do NOT add delta again, otherwise snapping never matches correctly.
  const newLeft = movingBox.x;
  const newTop = movingBox.y;
  const newRight = newLeft + movingBox.w;
  const newBottom = newTop + movingBox.h;
  const cx = newLeft + movingBox.w / 2;
  const cy = newTop + movingBox.h / 2;

  if (DEBUG_ALIGN) {
    console.log('[ALIGN] computeMoveSnap: input', {
      box: { x: movingBox.x, y: movingBox.y, w: movingBox.w, h: movingBox.h },
      deltaX,
      deltaY,
      threshold,
      candidates: candidates.length,
    });
  }

  const snapX = computeSnapDeltaForAxis(
    { start: newLeft, center: cx, end: newRight },
    guides.vx,
    threshold,
    guides.vxMap
  );
  const snapY = computeSnapDeltaForAxis(
    { start: newTop, center: cy, end: newBottom },
    guides.vy,
    threshold,
    guides.vyMap
  );

  if (DEBUG_ALIGN) {
    console.log('[ALIGN] snaps', {
      X: {
        matched: snapX.matched,
        type: snapX.type,
        target: snapX.target,
        delta: snapX.delta,
      },
      Y: {
        matched: snapY.matched,
        type: snapY.type,
        target: snapY.target,
        delta: snapY.delta,
      },
    });
  }

  // Object-based snapping result (relative to input delta)
  const objAdjDx = snapX.matched ? deltaX + snapX.delta : deltaX;
  const objAdjDy = snapY.matched ? deltaY + snapY.delta : deltaY;

  // Optional grid snapping inside frame: choose the smaller correction per axis
  let bestDx = objAdjDx;
  let bestDy = objAdjDy;
  // Consider "matched" even when correction is 0 (already aligned)
  let useObjX = snapX.matched;
  let useObjY = snapY.matched;
  let useGridX = false;
  let useGridY = false;
  let gridResLocal = null;
  const bestLines = [];
  try {
    const firstMoving =
      (cache?.movingNodes && cache.movingNodes[0]?.node) ||
      (movingNodes && (movingNodes[0]?.node || movingNodes[0])) ||
      null;
    if (firstMoving && hasFrameAncestor(firstMoving, stageContext) && cache?.initialBox) {
      const frame = getFrameAncestor(firstMoving, stageContext);
      if (frame) {
        const currentForGrid = {
          left: cache.initialBox.x,
          top: cache.initialBox.y,
          width: cache.initialBox.w,
          height: cache.initialBox.h,
        };
        const gridRes =
          applyGridSnapping(frame, currentForGrid, deltaX, deltaY, stageContext) || null;
        if (gridRes) {
          const objDxShift = Math.abs(objAdjDx - deltaX);
          const objDyShift = Math.abs(objAdjDy - deltaY);
          const gridDxShift = Math.abs(gridRes.deltaX - deltaX);
          const gridDyShift = Math.abs(gridRes.deltaY - deltaY);
          useGridX = gridDxShift > 0 && gridDxShift < objDxShift;
          useGridY = gridDyShift > 0 && gridDyShift < objDyShift;
          bestDx = useGridX ? gridRes.deltaX : objAdjDx;
          bestDy = useGridY ? gridRes.deltaY : objAdjDy;
          useObjX = !useGridX && useObjX;
          useObjY = !useGridY && useObjY;
          gridResLocal = gridRes;
          if (DEBUG_ALIGN) {
            console.log('[ALIGN] grid compare', {
              objDxShift,
              objDyShift,
              gridDxShift,
              gridDyShift,
              useGridX,
              useGridY,
              gridLines: (gridRes.lines || []).length,
            });
          }
        }
      }
    }
  } catch {
    /* safe fallback to object snapping */
  }

  // Build connector segments (red) for whichever snapping source is selected per axis
  const absLeftAfter = (cache?.initialBox?.x ?? newLeft) + bestDx;
  const absTopAfter = (cache?.initialBox?.y ?? newTop) + bestDy;
  const selectedXSource = useGridX ? 'grid' : useObjX ? 'obj' : null;
  const selectedYSource = useGridY ? 'grid' : useObjY ? 'obj' : null;

  if (selectedXSource === 'obj') {
    const xFrom =
      snapX.type === 'start'
        ? absLeftAfter
        : snapX.type === 'end'
          ? absLeftAfter + movingBox.w
          : absLeftAfter + movingBox.w / 2;
    const xTo = snapX.target;
    const refRect = Array.isArray(snapX.refs) && snapX.refs.length ? snapX.refs[0].rect : null;
    const a1 = absTopAfter,
      a2 = absTopAfter + movingBox.h;
    const b1 = refRect ? refRect.y : a1,
      b2 = refRect ? refRect.y + refRect.h : a2;
    const ov1 = Math.max(a1, b1),
      ov2 = Math.min(a2, b2);
    const y = ov1 <= ov2 ? (ov1 + ov2) / 2 : absTopAfter + movingBox.h / 2;
    if (Math.abs(xFrom - xTo) < 1e-6) {
      // edges coincide: draw vertical segment spanning from far end to far end of both boxes
      const y1Far = Math.min(a1, b1);
      const y2Far = Math.max(a2, b2);
      bestLines.push({ type: 'segment', x1: xFrom, y1: y1Far, x2: xTo, y2: y2Far });
    } else {
      bestLines.push({ type: 'segment', x1: xFrom, y1: y, x2: xTo, y2: y });
    }
  }
  if (selectedYSource === 'obj') {
    const yFrom =
      snapY.type === 'start'
        ? absTopAfter
        : snapY.type === 'end'
          ? absTopAfter + movingBox.h
          : absTopAfter + movingBox.h / 2;
    const yTo = snapY.target;
    const refRect = Array.isArray(snapY.refs) && snapY.refs.length ? snapY.refs[0].rect : null;
    const a1 = absLeftAfter,
      a2 = absLeftAfter + movingBox.w;
    const b1 = refRect ? refRect.x : a1,
      b2 = refRect ? refRect.x + refRect.w : a2;
    const ov1 = Math.max(a1, b1),
      ov2 = Math.min(a2, b2);
    const x = ov1 <= ov2 ? (ov1 + ov2) / 2 : absLeftAfter + movingBox.w / 2;
    if (Math.abs(yFrom - yTo) < 1e-6) {
      // edges coincide: draw horizontal segment spanning from far end to far end of both boxes
      const x1Far = Math.min(a1, b1);
      const x2Far = Math.max(a2, b2);
      bestLines.push({ type: 'segment', x1: x1Far, y1: yFrom, x2: x2Far, y2: yTo });
    } else {
      bestLines.push({ type: 'segment', x1: x, y1: yFrom, x2: x, y2: yTo });
    }
  }

  // Draw grid target lines when grid was chosen for that axis
  if (gridResLocal) {
    const absLeftAfter = (cache?.initialBox?.x ?? newLeft) + bestDx;
    const absTopAfter = (cache?.initialBox?.y ?? newTop) + bestDy;

    if (selectedXSource === 'grid') {
      const vlines = (gridResLocal.lines || []).filter((l) => l.type === 'vertical');
      let chosen = null;
      let bestDist = Infinity;
      const targets = [absLeftAfter, absLeftAfter + movingBox.w / 2, absLeftAfter + movingBox.w];
      for (const l of vlines) {
        for (const t of targets) {
          const d = Math.abs((l.left ?? 0) - t);
          if (d < bestDist) {
            bestDist = d;
            chosen = l;
          }
        }
      }
      const xTo = chosen ? chosen.left : absLeftAfter;
      const xFrom =
        Math.abs(absLeftAfter - xTo) < Math.abs(absLeftAfter + movingBox.w / 2 - xTo)
          ? absLeftAfter
          : Math.abs(absLeftAfter + movingBox.w - xTo) <
              Math.abs(absLeftAfter + movingBox.w / 2 - xTo)
            ? absLeftAfter + movingBox.w
            : absLeftAfter + movingBox.w / 2;
      const a1 = absTopAfter,
        a2 = absTopAfter + movingBox.h;
      const y = (a1 + a2) / 2;
      if (Math.abs(xFrom - xTo) < 1e-6) {
        // edges coincide: show vertical span along current box
        bestLines.push({
          type: 'segment',
          x1: xFrom,
          y1: a1,
          x2: xTo,
          y2: a2,
          source: 'grid',
        });
      } else {
        bestLines.push({
          type: 'segment',
          x1: xFrom,
          y1: y,
          x2: xTo,
          y2: y,
          source: 'grid',
        });
      }
    }
    if (selectedYSource === 'grid') {
      const hlines = (gridResLocal.lines || []).filter((l) => l.type === 'horizontal');
      let chosen = null;
      let bestDist = Infinity;
      const targets = [absTopAfter, absTopAfter + movingBox.h / 2, absTopAfter + movingBox.h];
      for (const l of hlines) {
        for (const t of targets) {
          const d = Math.abs((l.top ?? 0) - t);
          if (d < bestDist) {
            bestDist = d;
            chosen = l;
          }
        }
      }
      const yTo = chosen ? chosen.top : absTopAfter;
      const yFrom =
        Math.abs(absTopAfter - yTo) < Math.abs(absTopAfter + movingBox.h / 2 - yTo)
          ? absTopAfter
          : Math.abs(absTopAfter + movingBox.h - yTo) <
              Math.abs(absTopAfter + movingBox.h / 2 - yTo)
            ? absTopAfter + movingBox.h
            : absTopAfter + movingBox.h / 2;
      const a1 = absLeftAfter,
        a2 = absLeftAfter + movingBox.w;
      const x = (a1 + a2) / 2;
      if (Math.abs(yFrom - yTo) < 1e-6) {
        // edges coincide: show horizontal span along current box
        bestLines.push({
          type: 'segment',
          x1: a1,
          y1: yFrom,
          x2: a2,
          y2: yTo,
          source: 'grid',
        });
      } else {
        bestLines.push({
          type: 'segment',
          x1: x,
          y1: yFrom,
          x2: x,
          y2: yTo,
          source: 'grid',
        });
      }
    }
  }
  const anySnap = (useObjX && snapX.matched) || (useObjY && snapY.matched) || useGridX || useGridY;
  const out = {
    delta: { left: bestDx, top: bestDy },
    lines: bestLines,
    highlightUntil: anySnap ? Date.now() + 140 : 0,
  };
  if (DEBUG_ALIGN) {
    console.log('[ALIGN] out', {
      delta: out.delta,
      lineTypes: out.lines.map((l) => l.type),
      segments: out.lines
        .filter((l) => l.type === 'segment')
        .map((s) => ({ x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2 })),
    });
  }
  return out;
}

// Resize snapping: adjust local resize deltas (deltaX, deltaY in node-local axes)
function computeResizeSnap(stageContext, movingBox, movingNodes, deltaX, deltaY, cache) {
  const threshold = getWorldSnapThreshold(stageContext);
  const candidates = cache?.candidates || buildCandidateRects(stageContext, movingNodes);
  const guides = cache?.guides || collectCandidateGuideValues(candidates);

  // Ensure we have an initial box stored in cache for consistent snapping during gesture
  if (!cache.initialBox) {
    if (!movingBox) {
      const r = stageContext.api.getNodeRegion(movingNodes[0].node || movingNodes[0]).rect;
      cache.initialBox = { x: r.x, y: r.y, w: r.w, h: r.h };
    } else {
      cache.initialBox = { x: movingBox.x, y: movingBox.y, w: movingBox.w, h: movingBox.h };
    }
  }

  const handleId =
    (cache?.movingNodes && cache.movingNodes[0]?.id) || (movingNodes && movingNodes[0]?.id) || '';

  // Base rect at gesture start (axis-aligned)
  const base = cache.initialBox;
  const left0 = base.x;
  const top0 = base.y;
  const right0 = base.x + base.w;
  const bottom0 = base.y + base.h;

  // Determine which edges move for this handle
  const movesLeft = handleId === 'left' || handleId === 'topLeft' || handleId === 'bottomLeft';
  const movesRight = handleId === 'right' || handleId === 'topRight' || handleId === 'bottomRight';
  const movesTop = handleId === 'top' || handleId === 'topLeft' || handleId === 'topRight';
  const movesBottom =
    handleId === 'bottom' || handleId === 'bottomLeft' || handleId === 'bottomRight';

  // Proposed new AABB after applying local deltas (approximation; good enough for snapping guides)
  const left1 = left0 + (movesLeft ? deltaX : 0);
  const right1 = right0 + (movesRight ? deltaX : 0);
  const top1 = top0 + (movesTop ? deltaY : 0);
  const bottom1 = bottom0 + (movesBottom ? deltaY : 0);

  // Build current triplets
  const tripletX = { start: left1, center: (left1 + right1) / 2, end: right1 };
  const tripletY = { start: top1, center: (top1 + bottom1) / 2, end: bottom1 };

  // Restrict which reference of the triplet is eligible for snapping per axis
  const allowXTypes = movesLeft ? new Set(['start']) : movesRight ? new Set(['end']) : new Set();
  const allowYTypes = movesTop ? new Set(['start']) : movesBottom ? new Set(['end']) : new Set();

  // Try edge/center snaps first
  let rawSnapX = allowXTypes.size
    ? computeSnapDeltaForAxis(tripletX, guides.vx, threshold, guides.vxMap)
    : { matched: false, delta: 0, type: null, target: null };
  let rawSnapY = allowYTypes.size
    ? computeSnapDeltaForAxis(tripletY, guides.vy, threshold, guides.vyMap)
    : { matched: false, delta: 0, type: null, target: null };

  // If not matched, try gap-based targets (spacing snapping)
  if (!rawSnapX.matched && allowXTypes.size && guides.gx) {
    rawSnapX = computeSnapDeltaForAxis(tripletX, guides.gx, threshold, guides.gxMap);
    if (rawSnapX && rawSnapX.matched) rawSnapX.source = 'gap';
  }
  if (!rawSnapY.matched && allowYTypes.size && guides.gy) {
    rawSnapY = computeSnapDeltaForAxis(tripletY, guides.gy, threshold, guides.gyMap);
    if (rawSnapY && rawSnapY.matched) rawSnapY.source = 'gap';
  }

  const snapX =
    rawSnapX.matched && allowXTypes.has(rawSnapX.type)
      ? rawSnapX
      : { matched: false, delta: 0, type: null, target: null };
  const snapY =
    rawSnapY.matched && allowYTypes.has(rawSnapY.type)
      ? rawSnapY
      : { matched: false, delta: 0, type: null, target: null };

  // Adjust local deltas by snap corrections (these deltas are in the same local basis as inputs)
  const adjDx = deltaX + (snapX.matched ? snapX.delta : 0);
  const adjDy = deltaY + (snapY.matched ? snapY.delta : 0);

  // Lines: use simple guide lines on target positions
  // Recompute new box using adjusted deltas to size the guide visuals
  const left2 = left0 + (movesLeft ? adjDx : 0);
  const right2 = right0 + (movesRight ? adjDx : 0);
  const top2 = top0 + (movesTop ? adjDy : 0);
  const bottom2 = bottom0 + (movesBottom ? adjDy : 0);
  const newBox = {
    left: left2,
    top: top2,
    width: Math.max(0, right2 - left2),
    height: Math.max(0, bottom2 - top2),
  };

  const lines = buildGuideLines(newBox, snapX, snapY);

  // Also draw connector segments (red dashed) like move snapping, for clearer guidance
  const cx2 = newBox.left + newBox.width / 2;
  const cy2 = newBox.top + newBox.height / 2;
  if (snapX && snapX.matched && typeof snapX.target === 'number') {
    const xFrom =
      snapX.type === 'start'
        ? newBox.left
        : snapX.type === 'end'
          ? newBox.left + newBox.width
          : cx2;
    const xTo = snapX.target;
    const refRect = Array.isArray(snapX.refs) && snapX.refs.length ? snapX.refs[0].rect : null;
    const a1 = newBox.top,
      a2 = newBox.top + newBox.height;
    const b1 = refRect ? refRect.y : a1,
      b2 = refRect ? refRect.y + refRect.h : a2;
    const ov1 = Math.max(a1, b1),
      ov2 = Math.min(a2, b2);
    const y = ov1 <= ov2 ? (ov1 + ov2) / 2 : cy2;
    if (Math.abs(xFrom - xTo) < 1e-6) {
      const y1Far = Math.min(a1, b1);
      const y2Far = Math.max(a2, b2);
      lines.push({
        type: 'segment',
        x1: xFrom,
        y1: y1Far,
        x2: xTo,
        y2: y2Far,
        source: (snapX && snapX.source) || 'obj',
      });
    } else {
      lines.push({
        type: 'segment',
        x1: xFrom,
        y1: y,
        x2: xTo,
        y2: y,
        source: (snapX && snapX.source) || 'obj',
      });
    }
  }
  if (snapY && snapY.matched && typeof snapY.target === 'number') {
    const yFrom =
      snapY.type === 'start' ? newBox.top : snapY.type === 'end' ? newBox.top + newBox.height : cy2;
    const yTo = snapY.target;
    const refRect = Array.isArray(snapY.refs) && snapY.refs.length ? snapY.refs[0].rect : null;
    const a1 = newBox.left,
      a2 = newBox.left + newBox.width;
    const b1 = refRect ? refRect.x : a1,
      b2 = refRect ? refRect.x + refRect.w : a2;
    const ov1 = Math.max(a1, b1),
      ov2 = Math.min(a2, b2);
    const x = ov1 <= ov2 ? (ov1 + ov2) / 2 : cx2;
    if (Math.abs(yFrom - yTo) < 1e-6) {
      const x1Far = Math.min(a1, b1);
      const x2Far = Math.max(a2, b2);
      lines.push({
        type: 'segment',
        x1: x1Far,
        y1: yFrom,
        x2: x2Far,
        y2: yTo,
        source: (snapY && snapY.source) || 'obj',
      });
    } else {
      lines.push({
        type: 'segment',
        x1: x,
        y1: yFrom,
        x2: x,
        y2: yTo,
        source: (snapY && snapY.source) || 'obj',
      });
    }
  }
  const out = {
    delta: { left: adjDx, top: adjDy },
    lines,
    highlightUntil: snapX.matched || snapY.matched ? Date.now() + 140 : 0,
  };
  return out;
}

// Stateless cache/result API (used by resize/section-move as well)
export function createAlignCache(
  stageContext,
  movingBox,
  movingNodes,
  deltaX,
  deltaY,
  point,
  operation
) {
  return {
    candidates: buildCandidateRects(stageContext, movingNodes),
    guides: null, // lazily built
    operation: operation || 'move',
    initialBox: movingBox
      ? { x: movingBox.x, y: movingBox.y, w: movingBox.w, h: movingBox.h }
      : null,
    movingNodes,
  };
}

export function createAlignResult(
  stageContext,
  movingBox,
  movingNodes,
  deltaX,
  deltaY,
  point,
  operation = 'move',
  cache = null
) {
  if (!movingNodes || !movingNodes.length) return null;
  if (!movingBox) {
    // derive from first node if not provided
    const r = stageContext.api.getNodeRegion(movingNodes[0].node || movingNodes[0]).rect;
    movingBox = { x: r.x, y: r.y, w: r.w, h: r.h };
  }
  const c =
    cache ||
    createAlignCache(stageContext, movingBox, movingNodes, deltaX, deltaY, point, operation);
  if (!c.guides) c.guides = collectCandidateGuideValues(c.candidates);

  if (operation === 'move') {
    return computeMoveSnap(stageContext, movingBox, movingNodes, deltaX, deltaY, c);
  }
  if (operation === 'resize') {
    return computeResizeSnap(stageContext, movingBox, movingNodes, deltaX, deltaY, c);
  }
  // Unsupported operation
  return null;
}

// Snap engine for move gesture
class SnapEngine {
  constructor(stageContext) {
    this.stageContext = stageContext;
    this.active = false;
    this.cache = null;
    this.last = null;
  }
  start({ box, movingNodes, deltaX = 0, deltaY = 0, point = null, operation = 'move' }) {
    this.active = true;
    this.cache = createAlignCache(
      this.stageContext,
      box,
      movingNodes,
      deltaX,
      deltaY,
      point,
      operation
    );
    // build guides up-front for speed during drag
    this.cache.guides = collectCandidateGuideValues(this.cache.candidates);
    this.last = null;
  }
  update({ box, deltaX = 0, deltaY = 0, _point = null }) {
    if (!this.active || !this.cache) return null;
    const res = computeMoveSnap(
      this.stageContext,
      box,
      this.cache.movingNodes || [],
      deltaX,
      deltaY,
      this.cache
    );
    this.last = res;
    return res;
  }
  getLast() {
    return this.last;
  }
  end() {
    this.active = false;
    this.cache = null;
    this.last = null;
  }
}

export function getOrCreateSnapEngine(stageContext) {
  const holder = stageContext.interactive || stageContext.providers?.interactive || stageContext;
  if (!holder._snapEngine) holder._snapEngine = new SnapEngine(stageContext);
  return holder._snapEngine;
}

// Visual helper: draw alignment guide lines from last result
export function drawAlignmentHelper(context, stageContext) {
  const result =
    stageContext?.interactive?.alignmentResult || getOrCreateSnapEngine(stageContext).getLast();
  const scale = stageContext.providers?.scale;
  if (!result || !result.lines || !result.lines.length) return;

  context.save();
  const now = Date.now();
  const isHighlight = result.highlightUntil && now < result.highlightUntil;
  const segments = result.lines.filter((l) => l.type === 'segment');
  if (segments.length) {
    // Choose color by source: obj/grid/gap
    const pickColor = (s) =>
      s.source === 'gap'
        ? 'rgba(255,165,0,1.0)'
        : s.source === 'grid'
          ? 'rgba(72, 187, 120, 1.0)'
          : 'rgba(255, 100, 100, 1.0)';
    context.lineWidth = isHighlight ? 3 : 2;
    context.setLineDash([8, 4]);
    for (const s of segments) {
      context.strokeStyle = pickColor(s);
      let x1 = s.x1 * scale.scale + scale.translateX;
      let y1 = s.y1 * scale.scale + scale.translateY;
      let x2 = s.x2 * scale.scale + scale.translateX;
      let y2 = s.y2 * scale.scale + scale.translateY;
      const dx = x2 - x1,
        dy = y2 - y1;
      const len = Math.hypot(dx, dy);
      if (len < 1) {
        // Fallback: draw a small 16px tick in screen space on the intended axis
        const isVertical = Math.abs(s.x1 - s.x2) < 1e-6;
        if (isVertical) {
          x2 = x1;
          y1 = y1 - 8;
          y2 = y2 + 8;
        } else {
          y2 = y1;
          x1 = x1 - 8;
          x2 = x2 + 8;
        }
      }
      if (DEBUG_ALIGN) {
        console.log('[ALIGN] draw segment screen', { x1, y1, x2, y2 });
      }
      context.beginPath();
      context.moveTo(x1, y1);
      context.lineTo(x2, y2);
      context.stroke();
    }
  } else {
    context.lineWidth = isHighlight ? 2.5 : 1.5;
    context.setLineDash([6, 3]);
    for (const line of result.lines) {
      const color =
        line.source === 'gap'
          ? 'rgba(255,165,0,1.0)'
          : line.source === 'grid'
            ? 'rgba(72, 187, 120, 1.0)'
            : 'rgba(139, 62, 255, 1.0)';
      context.strokeStyle = color;
      if (line.type === 'vertical') {
        const x = line.left * scale.scale + scale.translateX;
        const y1 = line.top * scale.scale + scale.translateY;
        const y2 = (line.top + line.size) * scale.scale + scale.translateY;
        context.beginPath();
        context.moveTo(x, y1);
        context.lineTo(x, y2);
        context.stroke();
      } else if (line.type === 'horizontal') {
        const y = line.top * scale.scale + scale.translateY;
        const x1 = line.left * scale.scale + scale.translateX;
        const x2 = (line.left + line.size) * scale.scale + scale.translateX;
        context.beginPath();
        context.moveTo(x1, y);
        context.lineTo(x2, y);
        context.stroke();
      }
    }
  }
  context.restore();
}

// Re-export: used by Layer.vue
export const sortByRenderIndex = _sortByRenderIndex;
